import React, { useState, useEffect } from "react";
import { Box, Stepper, Step, StepLabel, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../../../store/Users/usersThunks";
import InfoStep from "./InfoStep";
import FiltersStep from "./FiltersStep";
import ShareStep from "./ShareStep";
import SummaryStep from "./SummaryStep";

function CreatePlaylistForm({
  onSubmit,
  activeStep,
  setActiveStep,
  initialData = {},
  filterModel,
  selectedIds = [],
}) {
  const currentUser = useSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    ...initialData,
    name: initialData.name || "",
    description: initialData.description || "",
    isPublic: initialData.isPublic !== undefined ? initialData.isPublic : false,
    selectedUsers:
      initialData.selectedUsers || (currentUser ? [currentUser.id] : []),
    playlistType:
      initialData.playlistType !== undefined
        ? initialData.playlistType
        : "incident_accident",
    ppsNumber: initialData.ppsNumber || "",
    worksite: initialData.worksite || "",
    notes: initialData.notes || "",
    possessionManager: initialData.possessionManager || "",
    worksiteManager: initialData.worksiteManager || "",
    fatality: initialData.fatality || false,
  });

  const [formErrors, setFormErrors] = useState({});
  const steps = ["Info", "Filters", "Share", "Summary"];
  const dispatch = useDispatch();
  const reduxUsers = useSelector((state) => state.users.users);
  const usersLoading = useSelector((state) => state.users.loading);

  useEffect(() => {
    if (activeStep === 2 && (!reduxUsers || reduxUsers.length === 0)) {
      dispatch(fetchUsers({}));
    }
  }, [activeStep, reduxUsers, dispatch]);

  const validateCurrentStep = () => {
    const errors = {};
    if (activeStep === 0) {
      const {
        name,
        description,
        playlistType,
        ppsNumber,
        worksite,
        possessionManager,
        worksiteManager,
      } = formData;

      if (!name) errors.name = "Name is required";
      if (!description) errors.description = "Description is required";

      // New: At least one of PPS Number or Worksite is required
      if (!ppsNumber && !worksite) {
        errors.ppsNumber = "Either PPS Number or Worksite is required";
        errors.worksite = "Either PPS Number or Worksite is required";
      }

      // Additional review-type-specific fields
      if (playlistType === "incident_accident") {
        if (!possessionManager)
          errors.possessionManager = "Possession Manager is required";
        if (!worksiteManager)
          errors.worksiteManager = "Worksite Manager is required";
      }
    }
    if (activeStep === 1) {
      const filtersEmpty =
        !filterModel || !filterModel.items || filterModel.items.length === 0;
      const selectedEmpty = !selectedIds || selectedIds.length === 0;
      if (filtersEmpty && selectedEmpty) {
        errors.filters =
          "Please apply at least one filter or select at least one row before proceeding.";
      }
    }
    if (activeStep === 2) {
      if (
        !formData.isPublic &&
        (!formData.selectedUsers || formData.selectedUsers.length === 0)
      ) {
        errors.selectedUsers = "Please select at least one user to share with.";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setFormErrors({});
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleFinalSubmit = () => {
    if (!validateCurrentStep()) return;

    const finalRecordedCallFilters =
      filterModel && filterModel.items ? filterModel.items : [];

    const finalData = {
      playlistName: formData.name,
      playlistDescription: formData.description,
      isPublic: formData.isPublic,
      recordedCallFilters: finalRecordedCallFilters,
      recordedCallIds: selectedIds,
      sharedUserIds: formData.selectedUsers,

      // New review-related fields
      playlistType: formData.playlistType,
      ppsNumber: formData.ppsNumber,
      worksite: formData.worksite,
    };

    // Conditionally include optional fields
    if (formData.playlistType === "close_call") {
      finalData.notes = formData.notes;
    }

    if (formData.playlistType === "incident_accident") {
      finalData.possessionManager = formData.possessionManager;
      finalData.worksiteManager = formData.worksiteManager;
      finalData.fatality = formData.fatality;
    }

    // Continue with submit
    onSubmit(finalData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData((prev) => {
      const selectedUsers = prev.selectedUsers || [];
      return selectedUsers.includes(userId)
        ? {
            ...prev,
            selectedUsers: selectedUsers.filter((id) => id !== userId),
          }
        : { ...prev, selectedUsers: [...selectedUsers, userId] };
    });
  };

  const toggleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers:
        prev.selectedUsers.length === reduxUsers.length
          ? []
          : reduxUsers.map((user) => user.id),
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <InfoStep
            formData={formData}
            formErrors={formErrors}
            handleChange={handleChange}
          />
        );
      case 1:
        return (
          <FiltersStep filterModel={filterModel} selectedIds={selectedIds} />
        );
      case 2:
        return (
          <ShareStep
            formData={formData}
            setFormData={setFormData}
            reduxUsers={reduxUsers}
            usersLoading={usersLoading}
            formErrors={formErrors}
            toggleUserSelection={toggleUserSelection}
            toggleSelectAll={toggleSelectAll}
            currentUser={currentUser}
          />
        );
      case 3:
        return (
          <SummaryStep
            formData={formData}
            filterModel={filterModel}
            selectedIds={selectedIds}
            reduxUsers={reduxUsers}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {renderStepContent()}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 4 }}>
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack} fullWidth>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} fullWidth>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleFinalSubmit} fullWidth>
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default CreatePlaylistForm;
